import {AfterViewInit, ChangeDetectorRef, Component, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {MatCheckboxChange, MatDialog, MatDialogConfig, MatSnackBar} from '@angular/material';

import { AgmMap } from '@agm/core';
import { GoogleMap } from '@agm/core/services/google-maps-types';

import { Business } from '../models/business.model';
import { GoogleService } from '../../google/google.service';
import { LatLon } from '../../util/geodesy/latlon';
import { UserService } from '../../shared/user/user.service';
import { EditOverviewComponent } from '../edit-overview/edit-overview.component';
import { EditToggleService } from '../../shared/services/edit-toggle.service';
import { BusinessManagerService } from '../../shared/user/business-manager.service';
import { TagService } from '../../shared/services/tag.service';
import { formatFullDate } from '../../util/datetime-utils';
import * as moment from 'moment-timezone';
import { Router } from '@angular/router';
import { hasTag } from '../../util/tag-utils';
import { BlueBoxDialogComponent } from '../blue-box-dialog/blue-box-dialog.component';
import { SuggestAChangeComponent } from '../../shared/suggest-a-change/suggest-a-change.component';
import { SelectMagazineDialogComponent } from '../select-magazine-dialog/select-magazine-dialog.component';


@Component({
  selector: 'app-business-overview',
  templateUrl: './business-overview.component.html',
  styleUrls: ['./business-overview.component.scss']
})
export class BusinessOverviewComponent implements OnInit, AfterViewInit {
  @Input() business: Business;
  isBusinessManager = false;
  isFlyingClub = false;
  showMap = false;
  latlon: LatLon;
  latlonApprox = false;
  bounds:  google.maps.LatLngBoundsLiteral;
  editModeSet = false;
  adminDate: moment = null;
  ADMIN_ID = 49035;
  TIME_ZONE = 'America/New_York';
  ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
  isAmbassador = false;
  isRegionalManager = false;
  isFlightSchool = false;
  isFbo = false;

  @ViewChild('map') map: AgmMap;

  private _nativeMap: GoogleMap;
  constructor(
    private _google: GoogleService,
    private _userService: UserService,
    private _cdref: ChangeDetectorRef,
    private _editContactDialog: MatDialog,
    private _toggleService: EditToggleService,
    private _businessManagerService: BusinessManagerService,
    public snackBar: MatSnackBar,
    private _tagService: TagService,
    private _router: Router,
    private _blueBoxDialog: MatDialog,
    private _suggestAChangeDialog: MatDialog,
    private _selectMagazineDialog: MatDialog,
    public _snackBar: MatSnackBar
  ) {}

  async ngOnInit() {
    if (this.business.location) {
      this.latlon = new LatLon(this.business.location.lat, this.business.location.lon);
      this.showMap = true;
    } else if (this.business.address) {
      const address = this.business.address.formatAddress(true);
      if (address) {
        this.geocode(address);
      }
    }
    this._toggleService.toggleEmitter.subscribe(isOn => {
      this.editModeSet = isOn;
    });
    this.isBusinessManager = await this._businessManagerService.userOwnsBusiness(this.business.id);
    this.business.tags.map(tag => {
      if (tag.tagId === this.ADMIN_ID) {
        this.adminDate = moment.tz(tag.updateDate, this.TIME_ZONE);
        return;
      } else if (tag.tagName === 'Flying Club') {
        this.isFlyingClub = true;
      }
    });
    if (hasTag(this.business.tags, 'Flying Club') && this.business.flyingClub) {
      this.isAmbassador = await this._businessManagerService.isAmbassador();
    }
    this.isRegionalManager = await this._businessManagerService.isRegionalManager()
      && !await this._businessManagerService.userOwnsBusiness(this.business.id);
    this.isFlightSchool = (!this.business.tags.some(t => t.tagName.startsWith('Flight Training::Private'))
      && this.business.tags.some(t => t.tagName.startsWith('Flight Training')));
    this.isFbo = hasTag(this.business.tags, 'Admin::BusinessIsFbo');
  }

  async ngAfterViewInit() {
    if (this.showMap && this.latlon) {
      const nativeMap = await this._initMap();
      nativeMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
    }
  }

  private async _initMap(): Promise<GoogleMap> {
    return new Promise<GoogleMap>((resolve, reject) => {
      this.map.mapReady.subscribe(
        map => {
          this._nativeMap = map;
          resolve(map);
        },
        err => reject(err)
      );
    });
  }

  get BusinessWebAddress(): string {
    if (this.business.webAddress.indexOf('http://') >= 0 || this.business.webAddress.indexOf('https://') >= 0) {
      return this.business.webAddress;
    } else {
      return 'http://' + this.business.webAddress;
    }
  }

  get BusinessEmailAddress(): string {
    return 'mailto:' + this.business.email;
  }

  private async geocode(address: string) {
    await this._google.promise;
    const gc = await this._google.geocode({ address : address});
    if (gc[0]) {
      this.latlonApprox = true;
      this.showMap = true;
      this._cdref.detectChanges();
      const nativeMap = await this._initMap();
      const geom = gc[0].geometry;
      switch (geom.location_type) {
        case google.maps.GeocoderLocationType.ROOFTOP:
        case google.maps.GeocoderLocationType.RANGE_INTERPOLATED:
          this.latlon = new LatLon(geom.location.lat(), geom.location.lng());
          nativeMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
          break;
        case google.maps.GeocoderLocationType.GEOMETRIC_CENTER:
          // When only the geometric center is available, Google returns a location instead
          // of bounds. However, since the location is approximate, instead of showing a marker,
          // we center the map on the location and zoom out a bit.
          const latlon = new LatLon(geom.location.lat(), geom.location.lng());
          nativeMap.setCenter({ lat: latlon.lat, lng: latlon.lon });
          // Add an offset around the center to force the map to zoom out a bit
          const offset = 0.08;
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(new google.maps.LatLng(latlon.lat + offset, latlon.lon + offset));
          bounds.extend(new google.maps.LatLng(latlon.lat - offset, latlon.lon - offset));
          nativeMap.fitBounds(bounds as any);
          break;
        default:
          if (geom.bounds) {
            // When only approximate location is available, use bounds instead of lat/lon
            this.bounds = geom.bounds.toJSON();
            nativeMap.fitBounds(this.bounds);
          } else {
            this.showMap = false;
            this.latlonApprox = false;
          }
      }
    }
  }

  @HostListener('window:resize')
  async onWindowResize() {
    if (this.map && this._nativeMap) {
      await this.map.triggerResize();
      if (this.latlon) {
        this._nativeMap.setCenter({ lat: this.latlon.lat, lng: this.latlon.lon });
      } else if (this.bounds) {
        this._nativeMap.fitBounds(this.bounds);
      }
    }
  }

  /**
   * formats admin date
   * @return {string}
   */
  getAdminDate() {
    if (this.adminDate) {
      return formatFullDate(this.adminDate, this.TIME_ZONE);
    }
  }

  async checkBoxChanged(event: MatCheckboxChange) {
    try {
      let data: any;
      if (event.checked && this.adminDate) {
        data = await this._tagService.putTag(this.ADMIN_ID, this.business.id);
      } else if (event.checked) {
        data = await this._tagService.postTag(this.ADMIN_ID, this.business.id);
      }
      if (data) {
        this.adminDate = moment.tz(data.updateDate, this.TIME_ZONE);
        event.source.disabled = true;
      } else {
        throw new Error('API did not return any data');
      }
    } catch (e) {
      this.snackBar.open(this.ERROR_MSG, 'Error', {
        duration: 6000
      });
    }
  }

  /**
   * Opens modal for editing business overview section, displays snackbar if serverAPI was successfully updated.
   */
  openEditContactDialog(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      business: this.business
    };

    dialogConfig.width = '95%';
    const dialogRef = this._editContactDialog.open(EditOverviewComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this.snackBar.open('Business Info', 'Updated', {
          duration: 1000
        });
      } else {
        this.snackBar.open(this.ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  /**
   * Opens modal for blue box text.
   */
  openBlueBoxDialog(dialogType: string): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      dialogType: dialogType
    };

    dialogConfig.maxWidth = '600px';
    dialogConfig.maxHeight = '500px';
    const dialogRef = this._blueBoxDialog.open(BlueBoxDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
    });
  }

  async pinDropped(event: any) {
    if (this.business.location && this.business.location.lat && this.business.location.lon) {
      Object.assign(this.business.location, new LatLon(event.coords.lat, event.coords.lng));
    } else {
      this.business.location = new LatLon (event.coords.lat, event.coords.lng);
    }
    await this._businessManagerService.updateBusiness(this.business);
  }

  navigateToWebcams() {
    let tempUrl = this._router.url;
    if (this._router.url.indexOf('?') > 0) {
      tempUrl = this._router.url.substr(0, tempUrl.indexOf('?'));
    }
    if (this._router.url.indexOf('#') > 0) {
      tempUrl = this._router.url.substr(0, tempUrl.indexOf('#'));
    }
    this._router.navigate([tempUrl], {fragment: 'webcams'});
  }

  openSuggestAChangeDialog() {
    const dialogConfig = new MatDialogConfig();
    const ERROR_MSG = 'An error has occurred, please try back later, or call 1-800-USA-AOPA if the error persists.';
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '35%';
    dialogConfig.data = {
      businessID: this.business.id
    };
    const dialogRef = this._suggestAChangeDialog.open(SuggestAChangeComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((isUpdated) => {
      if (!isUpdated) { return; } // required to handle backdrop click.
      if (isUpdated.cancelBtnClicked) { return; }
      // isUpdated.success will be false if there is an error during any of the api calls.
      if (isUpdated.success) {
        this._snackBar.open('The information you have provided has been forwarded to the AOPA Airport Directory team and ' +
          'will be reviewed as soon as possible. Thank you!', 'Success', {
          duration: 6000
        });
      } else {
        this._snackBar.open(ERROR_MSG, 'Error', {
          duration: 6000
        });
      }
    });
  }

  openSelectMagDialog(facilityType: string): void {
    const dialogRef = this._selectMagazineDialog.open(SelectMagazineDialogComponent, {
      disableClose: false,
      autoFocus: true,
      data: {
        business: this.business
      }
    });
  }
}
